import { Model_ConfigurationsGet_SystemProductConfigurationItem, Model_LabelAndReferenceGet } from "src/backend/openapi/v1";
import { TGroupFieldKey, TGroupNode, TGroupSortBy, TSortDirection } from "../../configurator-types";

export type TFlatProductItem = {
  configuration: {
    // configurationId was renamed to systemProductConfigurationId on the list-view model
    systemProductConfigurationId?: string | null;
    systemProductConfigurationName?: string | null;
    // systemProductConfigurationItemId replaces projectLineItemId on missing/invalid info models
    missingInformation?: { systemProductConfigurationItemId?: string | null }[] | null;
    invalidInformation?: { systemProductConfigurationItemId?: string | null }[] | null;
  };
  projectLineItem: Model_ConfigurationsGet_SystemProductConfigurationItem;
};

type TProductGroupNode = TGroupNode<TFlatProductItem>;

// One level's unsorted buckets, before group-sort is applied and before deciding whether to recurse further
type TProductBucket = { key: string; label: string; items: TFlatProductItem[] };

type TGroupTranslations = {
  groupByMissingInfoYes: string;
  groupByMissingInfoNo: string;
  groupByDateToday: string;
  groupByDateThisWeek: string;
  groupByDateThisMonth: string;
  groupByDateOlder: string;
  groupByUnlabelled: string;
};

function getDateBucket(dateStr: string | null | undefined, t: TGroupTranslations): { key: string; label: string } {
  if (!dateStr) return { key: "older", label: t.groupByDateOlder };

  const date = new Date(dateStr);
  const now = new Date();

  // same calendar day = today
  if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()) {
    return { key: "today", label: t.groupByDateToday };
  }

  // ISO week starting Monday
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  startOfWeek.setHours(0, 0, 0, 0);
  if (date >= startOfWeek) {
    return { key: "thisWeek", label: t.groupByDateThisWeek };
  }

  // same calendar month
  if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) {
    return { key: "thisMonth", label: t.groupByDateThisMonth };
  }

  return { key: "older", label: t.groupByDateOlder };
}

// Partitions items into unsorted buckets for exactly one field — the reusable per-level step
// that groupProductsHierarchy recurses with. label grouping: a product with N labels appears in
// N buckets; no-label → "unlabelled" bucket.
function partitionProductsOnce(items: TFlatProductItem[], groupField: TGroupFieldKey, labelIndex: Map<string, Model_LabelAndReferenceGet[]>, t: TGroupTranslations): TProductBucket[] {
  const groupMap = new Map<string, TProductBucket>();

  const addToGroup = (key: string, label: string, item: TFlatProductItem) => {
    // Read back the just-set/just-existing bucket instead of asserting non-null on the Map lookup
    const existing = groupMap.get(key);
    if (existing) existing.items.push(item);
    else groupMap.set(key, { key, label, items: [item] });
  };

  // cache missingIds Sets per configuration to avoid rebuilding them per item
  const missingIdsCache = new Map<string, Set<string>>();
  const getMissingIds = (configId: string | null | undefined, missingInfo: { systemProductConfigurationItemId?: string | null }[] | null | undefined): Set<string> => {
    const key = configId ?? "";
    const cached = missingIdsCache.get(key);
    if (cached) return cached;
    // systemProductConfigurationItemId replaces projectLineItemId on missing info model
    const ids = new Set((missingInfo ?? []).map(m => m.systemProductConfigurationItemId ?? ""));
    missingIdsCache.set(key, ids);
    return ids;
  };

  // cache invalidIds Sets per configuration — same pattern as missingIdsCache
  const invalidIdsCache = new Map<string, Set<string>>();
  const getInvalidIds = (configId: string | null | undefined, invalidInfo: { systemProductConfigurationItemId?: string | null }[] | null | undefined): Set<string> => {
    const key = configId ?? "";
    const cached = invalidIdsCache.get(key);
    if (cached) return cached;
    // systemProductConfigurationItemId replaces projectLineItemId on invalid info model
    const ids = new Set((invalidInfo ?? []).map(m => m.systemProductConfigurationItemId ?? ""));
    invalidIdsCache.set(key, ids);
    return ids;
  };

  for (const item of items) {
    const { projectLineItem, configuration } = item;

    if (groupField === "productName") {
      const name = projectLineItem.productDescription ?? "Unknown";
      addToGroup(name, name, item);
    } else if (groupField === "productType") {
      const name = projectLineItem.productTypeName ?? "Unknown";
      addToGroup(name, name, item);
    } else if (groupField === "configurationName") {
      const name = configuration.systemProductConfigurationName ?? "Unknown";
      addToGroup(name, name, item);
    } else if (groupField === "missingInfo") {
      // systemProductConfigurationId replaces configurationId; systemProductConfigurationId is the item key
      const missingIds = getMissingIds(configuration.systemProductConfigurationId, configuration.missingInformation);
      const hasMissing = missingIds.has(projectLineItem.systemProductConfigurationItemId ?? "");
      const key = hasMissing ? "yes" : "no";
      addToGroup(key, hasMissing ? t.groupByMissingInfoYes : t.groupByMissingInfoNo, item);
    } else if (groupField === "invalidInfo") {
      // systemProductConfigurationId replaces configurationId; systemProductConfigurationId is the item key
      const invalidIds = getInvalidIds(configuration.systemProductConfigurationId, configuration.invalidInformation);
      const hasInvalid = invalidIds.has(projectLineItem.systemProductConfigurationItemId ?? "");
      const key = hasInvalid ? "yes" : "no";
      // reuse missingInfo bucket labels (Yes/No) — no separate invalidInfo bucket keys needed
      addToGroup(key, hasInvalid ? t.groupByMissingInfoYes : t.groupByMissingInfoNo, item);
    } else if (groupField === "date") {
      // cast because createdOn was added to the DTO after initial OpenAPI gen; use direct access if the field is already typed
      const createdOn = (projectLineItem as unknown as { createdOn?: string | null }).createdOn;
      const bucket = getDateBucket(createdOn, t);
      addToGroup(bucket.key, bucket.label, item);
    } else if (groupField === "label") {
      // systemProductConfigurationId is the item key on the list-view model
      const pliLabels = labelIndex.get(projectLineItem.systemProductConfigurationItemId ?? "") ?? [];
      if (pliLabels.length === 0) {
        addToGroup("unlabelled", t.groupByUnlabelled, item);
      } else {
        for (const lbl of pliLabels) addToGroup(lbl.shortname, lbl.shortname, item);
      }
    } else if (groupField === "configurationLabel") {
      // systemProductConfigurationId replaces configurationId on the list-view model
      const configLabels = labelIndex.get(configuration.systemProductConfigurationId ?? "") ?? [];
      if (configLabels.length === 0) {
        addToGroup("unlabelled", t.groupByUnlabelled, item);
      } else {
        for (const lbl of configLabels) addToGroup(lbl.shortname, lbl.shortname, item);
      }
    }
  }

  return Array.from(groupMap.values());
}

// Orders a single level's buckets by the user's group-sort setting. "date" buckets are the one
// exception: they have an inherent chronological order (today/this week/this month/older) that
// alphabetical or count sorting would scramble, so that field always ranks chronologically
// regardless of groupSortBy/groupSortDirection — matching this field's pre-hierarchy behavior.
function sortProductBuckets(buckets: TProductBucket[], groupField: TGroupFieldKey, groupSortBy: TGroupSortBy, groupSortDirection: TSortDirection): TProductBucket[] {
  if (groupField === "date") {
    const dateRank = ["today", "thisWeek", "thisMonth", "older"];
    return [...buckets].sort((a, b) => dateRank.indexOf(a.key) - dateRank.indexOf(b.key));
  }

  const compare =
    groupSortBy === "count"
      ? (a: TProductBucket, b: TProductBucket) => a.items.length - b.items.length
      : (a: TProductBucket, b: TProductBucket) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" });

  const sorted = [...buckets].sort(compare);
  return groupSortDirection === "asc" ? sorted : sorted.reverse();
}

// Recursively groups items into a multi-level hierarchy, one level per entry in groupFields
// (top-level first). Each level's buckets are sorted per sortProductBuckets before recursing
// into the next field, so group order at every depth reflects the same shared group-sort setting.
// Sorting of items *within* the deepest (leaf) bucket is the caller's job (unchanged: Products-tab
// item sort already runs before grouping) — this function only ever reorders buckets, never items.
export function groupProductsHierarchy(
  items: TFlatProductItem[],
  groupFields: TGroupFieldKey[],
  labelIndex: Map<string, Model_LabelAndReferenceGet[]>,
  t: TGroupTranslations,
  groupSortBy: TGroupSortBy,
  groupSortDirection: TSortDirection,
  depth = 0
): TProductGroupNode[] {
  const [field, ...restFields] = groupFields;
  const buckets = sortProductBuckets(partitionProductsOnce(items, field, labelIndex, t), field, groupSortBy, groupSortDirection);

  return buckets.map(bucket =>
    restFields.length === 0
      ? { key: bucket.key, label: bucket.label, depth, items: bucket.items }
      : { key: bucket.key, label: bucket.label, depth, children: groupProductsHierarchy(bucket.items, restFields, labelIndex, t, groupSortBy, groupSortDirection, depth + 1) }
  );
}
